/**
 * SalesReportsTab - Comprehensive sales analytics
 * Top sellers, item-level sales, and summary reports
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, Package, BarChart3, 
  Calendar, RefreshCw, ChevronDown, ChevronRight, Award,
  ArrowUpRight, ArrowDownRight, Minus, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

// Move TrendBadge outside of main component to avoid re-renders
const TrendBadge = ({ value }) => {
  if (value > 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
        <ArrowUpRight className="w-3 h-3" />
        {value.toFixed(1)}%
      </span>
    );
  } else if (value < 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">
        <ArrowDownRight className="w-3 h-3" />
        {Math.abs(value).toFixed(1)}%
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
      <Minus className="w-3 h-3" />
      0%
    </span>
  );
};

export default function SalesReportsTab() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [topSellers, setTopSellers] = useState(null);
  const [salesSummary, setSalesSummary] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [view, setView] = useState('top-sellers'); // top-sellers, item-details, summary

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const [topRes, summaryRes, detailRes] = await Promise.all([
        api.get(`/admin/analytics/top-sellers?period=${period}&limit=20`),
        api.get('/admin/analytics/sales-summary'),
        api.get(`/admin/analytics/item-sales-detail?period=${period}`)
      ]);
      
      setTopSellers(topRes.data);
      setSalesSummary(summaryRes.data);
      setItemDetails(detailRes.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      toast.error('Failed to load reports');
    }
    setLoading(false);
  }, [period]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({
      ...prev,
      [cat]: !prev[cat]
    }));
  };

  if (loading && !salesSummary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-reports-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Sales Reports</h2>
          <p className="text-muted-foreground">Detailed breakdown of your sales performance</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchReports} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {salesSummary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">All-Time Orders</p>
                <Package className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-2xl font-bold mt-2">{salesSummary.all_time.total_orders.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">All-Time Revenue</p>
                <DollarSign className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold mt-2">${salesSummary.all_time.combined_revenue.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Avg Ticket</p>
                <BarChart3 className="w-5 h-5 text-purple-500" />
              </div>
              <p className="text-2xl font-bold mt-2">${salesSummary.all_time.avg_ticket_value}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Today</p>
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <p className="text-2xl font-bold mt-2">${salesSummary.today.revenue.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">{salesSummary.today.orders} orders</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">This Month</p>
                <Calendar className="w-5 h-5 text-teal-500" />
              </div>
              <p className="text-2xl font-bold mt-2">${salesSummary.this_month.revenue.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">{salesSummary.this_month.orders} orders</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button 
          variant={view === 'top-sellers' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setView('top-sellers')}
        >
          <Award className="w-4 h-4 mr-2" />
          Top 20 Sellers
        </Button>
        <Button 
          variant={view === 'item-details' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setView('item-details')}
        >
          <Package className="w-4 h-4 mr-2" />
          All Items
        </Button>
      </div>

      {/* Top Sellers View */}
      {view === 'top-sellers' && topSellers && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Top 20 Best Sellers - {topSellers.period_label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {topSellers.top_sellers.map((item, idx) => (
                <div 
                  key={item.item_id}
                  className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {item.rank}
                  </div>
                  {item.image && (
                    <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{item.name}</p>
                    <p className="text-sm text-muted-foreground">{item.category_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-foreground">{item.quantity_sold} sold</p>
                    <p className="text-sm text-green-600">${item.revenue.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* By Category Summary */}
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-medium mb-4">By Category</h4>
              <div className="space-y-2">
                {Object.entries(topSellers.by_category).map(([category, items]) => (
                  <div key={category} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <span className="font-medium">{category}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">{items.length} items</span>
                        {expandedCategories[category] ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </div>
                    </button>
                    {expandedCategories[category] && (
                      <div className="p-3 space-y-2 bg-background">
                        {items.map(item => (
                          <div key={item.item_id} className="flex items-center justify-between text-sm">
                            <span>{item.name}</span>
                            <span className="font-medium">{item.quantity_sold} sold - ${item.revenue}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Items View */}
      {view === 'item-details' && itemDetails && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              All Items Sales - {itemDetails.period_label}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {itemDetails.summary.items_with_sales} items sold, {itemDetails.summary.items_without_sales} with no sales
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(itemDetails.by_category).map(([category, items]) => (
                <div key={category} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(`detail-${category}`)}
                    className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <span className="font-medium">{category}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-green-600">
                        ${items.reduce((s, i) => s + i.current_revenue, 0).toFixed(2)}
                      </span>
                      {expandedCategories[`detail-${category}`] ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                  {expandedCategories[`detail-${category}`] && (
                    <div className="divide-y">
                      {items.map(item => (
                        <div key={item.item_id} className="flex items-center justify-between p-3 text-sm">
                          <div className="flex items-center gap-3">
                            {item.image && (
                              <img src={item.image} alt={item.name} className="w-10 h-10 rounded object-cover" />
                            )}
                            <div>
                              <p className="font-medium">{item.name}</p>
                              <p className="text-muted-foreground">${item.base_price}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-medium">{item.current_qty} sold</p>
                              <p className="text-green-600">${item.current_revenue}</p>
                            </div>
                            <TrendBadge value={item.qty_trend} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
